import {gql} from '@apollo/client';
import {Alert, Button, Center, Divider, Group, Loader, Modal, Stack} from '@mantine/core';
import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {AntibodyEnforcedSchemaSpec} from 'antibodies/schema-configuration/AntibodyEnforcedDataModelConstants';
import {enforcedFieldSpecToField} from 'antibodies/schema-configuration/AntibodyEnforcedSchemaSpecUtils';
import {AntibodyComponentSchemaInterfaceId} from 'antibodies/schema-configuration/AntibodySchemaInterfaceConstants';
import {
  SchemaFieldSelectorsForMigrationQuery,
  SchemaFieldSelectorsForMigrationQuery_schemaFieldSelectors_SchemaFieldSelectorNode as RegistrySchemaFieldSelector,
} from 'antibodies/schema-configuration/schema-migration/__generated/SchemaFieldSelectorsForMigrationQuery';
import {FieldMigrationMapping} from 'antibodies/schema-configuration/schema-migration/AntibodySchemaMigrationConstants';
import ConstraintMigrationPreview from 'antibodies/schema-configuration/schema-migration/ConstraintMigrationPreview';
import hasEnforcedDataModelChanges from 'antibodies/schema-configuration/schema-migration/hasEnforcedDataModelChanges';
import NamingStrategyMigrationPreview from 'antibodies/schema-configuration/schema-migration/NamingStrategyMigrationPreview';
import NewFieldsMigrationPreview from 'antibodies/schema-configuration/schema-migration/NewFieldsMigrationPreview';
import {hasAnyUnmappedOptions} from 'antibodies/schema-configuration/schema-migration/newFieldsMigrationUtils';
import {
  getRegistryEnabledNamingStrategies,
  getSelectableNamingStrategies,
  getShouldAddNamingStrategies,
} from 'antibodies/schema-configuration/schema-migration/schemaMigrationNamingStrategies';
import {
  ClientTagSchemaConstraint,
  RegistryLabelingStrategy,
  TagSchemaConstraint,
} from 'constants/RegistryConstants';
import {NamingStrategy} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';

export const SCHEMA_FIELD_SELECTORS_FOR_MIGRATION_QUERY = gql`
  query SchemaFieldSelectorsForMigrationQuery @skipAudit @engComponent(name: SCHEMAS) {
    schemaFieldSelectors {
      ... on SchemaFieldSelectorNode {
        ...SchemaFieldSelectorGraphQL
        registry {
          id
        }
      }
    }
  }
  ${getFragmentWrapper('SchemaFieldSelector').fragment}
`;

export interface AntibodyComponentMigrationPreviewModalProps {
  componentTypeName: string;
  componentInterfaceId: AntibodyComponentSchemaInterfaceId;
  registryId: string;
  enforcedSchemaSpec: AntibodyEnforcedSchemaSpec;
  existingFields: Array<SchemaField | ClientSchemaField>;
  existingConstraint: ClientTagSchemaConstraint | TagSchemaConstraint | null;
  existingNamingStrategies: Array<RegistryLabelingStrategy> | null;
  hasNameTemplate: boolean;
  folderItemType: string | null;
  onClose: () => void;
  onApply: (options: {
    namingStrategiesToAdd?: Array<NamingStrategy>;
    fieldMigrationMappings: Array<FieldMigrationMapping>;
  }) => void | Promise<void>;
  isApplying?: boolean;
  allRegistrySelectors: Array<RegistrySchemaFieldSelector>;
}

/**
 * Confirmation modal shown before applying a protein component type to an existing schema.
 * Previews the enforced changes the migration will make and lets the user confirm or cancel.
 */
function useNamingStrategiesToAdd(
  enforcedNamingStrategySpec: AntibodyEnforcedSchemaSpec['enforcedNamingStrategySpec'],
  existingNamingStrategies: Array<RegistryLabelingStrategy> | null,
  hasNameTemplate: boolean
): {
  shouldAddNamingStrategies: boolean;
  registryEnabledNamingStrategies: Array<NamingStrategy>;
  selectableNamingStrategies: Array<NamingStrategy>;
  selectedNamingStrategies: Array<NamingStrategy>;
  setSelectedNamingStrategies: (strategies: Array<NamingStrategy>) => void;
} {
  const shouldAddNamingStrategies = useMemo(
    () =>
      enforcedNamingStrategySpec != null &&
      getShouldAddNamingStrategies(enforcedNamingStrategySpec, existingNamingStrategies),
    [enforcedNamingStrategySpec, existingNamingStrategies]
  );
  // All deploy-enabled strategies in the spec — rendered as checkboxes (some may be disabled).
  const registryEnabledNamingStrategies = useMemo(
    () =>
      enforcedNamingStrategySpec != null
        ? getRegistryEnabledNamingStrategies(enforcedNamingStrategySpec)
        : [],
    [enforcedNamingStrategySpec]
  );
  // Subset compatible with this schema's name-template state — pre-checked and togglable.
  const selectableNamingStrategies = useMemo(
    () =>
      enforcedNamingStrategySpec != null
        ? getSelectableNamingStrategies(enforcedNamingStrategySpec, hasNameTemplate)
        : [],
    [enforcedNamingStrategySpec, hasNameTemplate]
  );
  const [selectedNamingStrategies, setSelectedNamingStrategies] =
    useState<Array<NamingStrategy>>(selectableNamingStrategies);
  useEffect(() => {
    setSelectedNamingStrategies(selectableNamingStrategies);
  }, [selectableNamingStrategies]);

  return {
    shouldAddNamingStrategies,
    registryEnabledNamingStrategies,
    selectableNamingStrategies,
    selectedNamingStrategies,
    setSelectedNamingStrategies,
  };
}

export function AntibodyComponentMigrationPreviewModal({
  componentTypeName,
  componentInterfaceId,
  registryId,
  enforcedSchemaSpec,
  existingFields,
  existingConstraint,
  existingNamingStrategies,
  hasNameTemplate,
  folderItemType,
  onClose,
  onApply,
  isApplying = false,
  allRegistrySelectors,
}: AntibodyComponentMigrationPreviewModalProps): JSX.Element {
  const {enforcedFieldSpecs, enforcedConstraintSpec, enforcedNamingStrategySpec} = enforcedSchemaSpec;
  const showsEnforcedChanges = hasEnforcedDataModelChanges(enforcedSchemaSpec);
  const enforcedFieldsToCreate = enforcedFieldSpecs.map((enforcedFieldSpec) =>
    enforcedFieldSpecToField(componentInterfaceId, enforcedFieldSpec, registryId)
  );
  const {
    shouldAddNamingStrategies,
    registryEnabledNamingStrategies,
    selectableNamingStrategies,
    selectedNamingStrategies,
    setSelectedNamingStrategies,
  } = useNamingStrategiesToAdd(enforcedNamingStrategySpec, existingNamingStrategies, hasNameTemplate);

  const [fieldMigrationMappings, setFieldMigrationMappings] = useState<Array<FieldMigrationMapping>>([]);

  const handleApplyClick = useCallback((): void => {
    onApply({
      ...(shouldAddNamingStrategies ? {namingStrategiesToAdd: selectedNamingStrategies} : {}),
      fieldMigrationMappings,
    });
  }, [onApply, fieldMigrationMappings, selectedNamingStrategies, shouldAddNamingStrategies]);

  const hasNoSelectableStrategies = shouldAddNamingStrategies && selectableNamingStrategies.length === 0;
  const hasNoneSelected = shouldAddNamingStrategies && selectedNamingStrategies.length === 0;
  // Block submission while any migratable field still has source options that need mapping.
  const hasUnmappedOptions =
    showsEnforcedChanges &&
    hasAnyUnmappedOptions(
      enforcedFieldsToCreate,
      enforcedFieldSpecs,
      existingFields,
      fieldMigrationMappings,
      allRegistrySelectors
    );
  const isApplyDisabled = isApplying || hasNoSelectableStrategies || hasNoneSelected || hasUnmappedOptions;

  return (
    <Modal
      title={`Apply ${componentTypeName} protein component type`}
      opened={true}
      onClose={onClose}
      size="lg"
      closeOnClickOutside={!isApplying}
      closeOnEscape={!isApplying}
      withCloseButton={!isApplying}
    >
      <Stack spacing="lg">
        <Alert>
          {showsEnforcedChanges ? (
            <>
              Applying the {componentTypeName} protein component type makes the following changes to this
              schema. This action cannot be undone.
            </>
          ) : (
            <>
              Assigns the {componentTypeName} protein component type. No other schema changes. This action
              cannot be undone.
            </>
          )}
        </Alert>

        {showsEnforcedChanges && (
          <>
            <NewFieldsMigrationPreview
              fieldsToCreate={enforcedFieldsToCreate}
              enforcedFieldSpecs={enforcedFieldSpecs}
              existingFields={existingFields}
              fieldMigrationMappings={fieldMigrationMappings}
              onMappingsChange={setFieldMigrationMappings}
              allRegistrySelectors={allRegistrySelectors}
            />

            {enforcedConstraintSpec != null && (
              <ConstraintMigrationPreview
                existingConstraint={existingConstraint}
                enforcedConstraintSpec={enforcedConstraintSpec}
                enforcedFields={enforcedFieldsToCreate}
                folderItemType={folderItemType}
              />
            )}

            {enforcedNamingStrategySpec != null && (
              <NamingStrategyMigrationPreview
                enforcedNamingStrategySpec={enforcedNamingStrategySpec}
                existingNamingStrategies={existingNamingStrategies}
                registryEnabledNamingStrategies={registryEnabledNamingStrategies}
                selectableNamingStrategies={selectableNamingStrategies}
                selectedNamingStrategies={selectedNamingStrategies}
                onSelectedNamingStrategiesChange={setSelectedNamingStrategies}
              />
            )}
          </>
        )}

        {hasNoSelectableStrategies && (
          <Alert variant="error">
            None of the required labeling strategies are available for this schema. Add a name template or
            enable the required strategies on this deployment.
          </Alert>
        )}

        <Divider />

        <Group position="right">
          <Button variant="secondary" onClick={onClose} disabled={isApplying}>
            Cancel
          </Button>
          <Button onClick={handleApplyClick} loading={isApplying} disabled={isApplyDisabled}>
            Apply
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default function QueryingAntibodyComponentMigrationPreviewModal(
  props: Omit<AntibodyComponentMigrationPreviewModalProps, 'allRegistrySelectors'>
): JSX.Element {
  const result = useBenchlingQuery<SchemaFieldSelectorsForMigrationQuery>(
    SCHEMA_FIELD_SELECTORS_FOR_MIGRATION_QUERY
  );
  // Until the selectors query succeeds we can't tell which source options need mapping, so we must
  // not render the interactive modal — an empty selector list would masquerade as "no options to
  // map", clearing the unmapped-options warning and enabling Apply prematurely. Show loading/error
  // states inside the modal shell instead.
  if (result.type !== QueryResultType.SUCCESS) {
    return (
      <Modal
        title={`Apply ${props.componentTypeName} protein component type`}
        opened={true}
        onClose={props.onClose}
        size="lg"
      >
        {result.type === QueryResultType.ERROR ? (
          <Alert variant="error">
            An error occurred while loading schema fields. Refresh the page and try again.
          </Alert>
        ) : (
          <Center>
            <Loader />
          </Center>
        )}
      </Modal>
    );
  }
  const allRegistrySelectors = result.data.schemaFieldSelectors
    .filter((s): s is RegistrySchemaFieldSelector => s.__typename === 'SchemaFieldSelectorNode')
    .filter((s) => s.registry.id === props.registryId);
  return <AntibodyComponentMigrationPreviewModal {...props} allRegistrySelectors={allRegistrySelectors} />;
}
